import { useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import he from 'he';
import { formatBytes } from '../../utilities/format';
import type { ResourceChatMessage } from '../../types/resource';
import styles from './ResourceRow.module.scss';

// Enlarged hover preview of the row thumbnail (Angular used uib-popover-html with
// popover-placement="left" + append-to-body to escape the row's overflow:hidden).
// Bounded by PREVIEW_MAX with width/height auto so it keeps the image's natural
// aspect ratio (like Bootstrap .img-thumbnail: max-width:100% + height:auto), rather
// than forcing a square — a tall email thumbnail stays an upright rectangle. 325
// matches prod's rendered size (popover max-width → ~250w/~325h for a portrait page).
const PREVIEW_MAX = 325;
const PREVIEW_GAP = 8;

function typeIcon(type: string | null | undefined): string {
  switch (type) {
    case 'container': return 'fa-archive';
    case 'phone image': return 'fa-file-archive';
    case 'extracted conversation': return 'fa-comments';
    case 'email': return 'fa-envelope';
    case 'file': return 'fa-file';
    case 'phone image metadata': return 'fa-mobile';
    case 'instant message': return 'fa-comment';
    case 'mms':
    case 'sms': return 'fa-sms';
    case 'voicemail': return 'fa-voicemail';
    case 'note': return 'fa-sticky-note';
    default: return 'fa-file';
  }
}

// Chat rows (Angular's separate `instant message` / `sms` / `mms` switch branches)
// label the row with a participant and preview the message body instead of showing
// the synthetic `chat-message-<id>.msg` filename and an empty folder path.
const CHAT_TYPES = new Set(['instant message', 'sms', 'mms']);

// Angular used from.name || from.id || to[0].name || to[0].id in all three branches.
function chatLabel(m: ResourceChatMessage): string {
  const to = m.to?.find((p) => p?.name || p?.id);
  return m.from?.name || m.from?.id || to?.name || to?.id || 'Unknown';
}

// Minimal row shape ResourceRow reads. Both the full domain `Resource` and the grid's
// `NormalizedRow` satisfy this, so the same component renders in the viewer's Related
// tab and in the resource grid's list view.
export interface ResourceRowData {
  type?: string | null;
  emailFrom?: string | null;
  title?: string | null;
  filename?: string | null;
  bytes?: number | null;
  collectionName?: string | null;
  folders?: string | null;
  createdDate?: number | string | null;
  isTopParent?: boolean | null;
  isTopParentResource?: boolean | null;
  directPdfThumbnailPath?: string | null;
  hadProcessingException?: boolean | null;
  hasVirus?: boolean | null;
  numberOfAncestors?: number | null;
  chatMessage?: ResourceChatMessage | null;
}

// Indent per ancestor level for nested resources (Angular used 50px; 24px reads cleaner).
const INDENT_PER_LEVEL = 24;

interface Props<T extends ResourceRowData> {
  resource: T;
  showCollection?: boolean;
  hideThumbnail?: boolean;
  /** Suppress the per-ancestor indent (Angular grids pass no-indent="true"). */
  noIndent?: boolean;
  /** Render a selection checkbox at the row's leading edge. */
  selectable?: boolean;
  /** Whether the selection checkbox is checked (only meaningful with `selectable`). */
  selected?: boolean;
  /** Locks the checkbox (checked, non-interactive) — used in select-all mode. */
  selectDisabled?: boolean;
  /** Toggle handler for the checkbox; fires without opening the viewer. */
  onToggleSelect?: (resource: T) => void;
  onClick?: (resource: T) => void;
  /** Highlights the row as the document currently open in the viewer. */
  active?: boolean;
}

export function ResourceRow<T extends ResourceRowData>({
  resource,
  showCollection,
  hideThumbnail,
  noIndent,
  selectable,
  selected,
  selectDisabled,
  onToggleSelect,
  onClick,
  active,
}: Props<T>) {
  const checkboxId = useId();
  const thumbRef = useRef<HTMLImageElement>(null);
  const [preview, setPreview] = useState<{ top: number; left: number; flip: boolean } | null>(null);
  const isEmail = resource.type === 'email';
  // Only treat a row as chat when the message object actually came back — otherwise
  // fall through to the generic filename rendering rather than showing "Unknown".
  const chat = CHAT_TYPES.has(resource.type ?? '') ? resource.chatMessage ?? null : null;
  // Angular only unescaped the mms branch (`_.unescape`), leaving `&amp;` literal in
  // sms/IM text; decode all three (same `he.decode` the conversation viewer uses).
  const chatText = chat?.text ? he.decode(chat.text).trim() : '';
  const hasTitle =
    resource.type === 'file' ||
    resource.type === 'extracted conversation' ||
    resource.type === 'note';

  // Top-right stamp mirrors Angular's resourceCellRenderer: only the email branch shows
  // the created date (and only for a top parent — otherwise nothing); every other type
  // shows the file size. Sits above the thumbnail (not inline with the title).
  const isTopParent = !!(resource.isTopParent || resource.isTopParentResource);
  const stamp = isEmail
    ? isTopParent && resource.createdDate
      ? resource.createdDate
      : null
    : resource.bytes != null
      ? formatBytes(resource.bytes)
      : null;
  const hasThumbnail = !hideThumbnail && !!resource.directPdfThumbnailPath;
  const hasRightColumn = stamp != null || hasThumbnail;

  const indent = noIndent ? 0 : (resource.numberOfAncestors ?? 0) * INDENT_PER_LEVEL;
  const statusTitle = resource.hasVirus
    ? 'Virus detected'
    : resource.hadProcessingException
      ? 'Processing exception'
      : undefined;

  // Anchor the preview to the left of the thumbnail (flip right if there isn't room
  // for a full-width preview), vertically centered on it. A CSS transform positions
  // the box from this anchor, so we don't need its rendered (aspect-dependent) size.
  // We deliberately do NOT clamp to the viewport: matching prod, the preview may run
  // partially off-screen near the top/bottom edges so the caret always keeps pointing
  // at the correct thumbnail (clamping would slide the box and misalign the caret).
  const showPreview = () => {
    const r = thumbRef.current?.getBoundingClientRect();
    const win = thumbRef.current?.ownerDocument.defaultView;
    if (!r || !win) return;
    const flip = r.left < PREVIEW_MAX + PREVIEW_GAP;
    setPreview({
      top: r.top + r.height / 2,
      left: flip ? r.right + PREVIEW_GAP : r.left - PREVIEW_GAP,
      flip,
    });
  };

  return (
    <div
      className={[
        styles.row,
        resource.hadProcessingException ? styles.warning : '',
        resource.hasVirus ? styles.danger : '',
        onClick ? styles.clickable : '',
      ].filter(Boolean).join(' ')}
      // aria-current drives both the highlight styling (ResourceRow.module.scss) and is
      // the stable, CSS-module-independent hook the tests assert on.
      aria-current={active ? 'true' : undefined}
      style={indent ? { marginLeft: indent } : undefined}
      title={statusTitle}
      data-testid="resource-row"
      onClick={onClick ? () => onClick(resource) : undefined}
    >
      <div className={styles.content}>
        {/* Header line: checkbox + icon + primary text (+ bytes for files) all inline, so
            the detail lines below align to the cell's left edge, not indented under it. */}
        <h4 className={styles.heading}>
          {selectable && (
            <div
              className={`checkbox checkbox-primary ${styles.checkbox}`}
              // Checking a row must not open the viewer (mirrors the Angular grid, which
              // ignores row-body clicks that originate inside .checkbox).
              onClick={(e) => e.stopPropagation()}
            >
              {/* .checkbox CSS (styles/_abc-checkbox.scss) hides the native input and draws
                  the box/check on the adjacent <label>, so the label is required to be visible. */}
              <input
                id={checkboxId}
                type="checkbox"
                checked={!!selected}
                disabled={selectDisabled}
                onChange={() => onToggleSelect?.(resource)}
                aria-label="Select resource"
                data-testid="row-select-checkbox"
              />
              <label htmlFor={checkboxId} />
            </div>
          )}
          <i className={`fas fa-lg ${typeIcon(resource.type)} ${styles.typeIcon}`} />
          <span className={`text-primary ${styles.headingText} ${styles.truncate}`} data-testid="resource-title-link">
            {isEmail ? resource.emailFrom : chat ? chatLabel(chat) : resource.filename}
          </span>
        </h4>
        {/* Chat rows carry no title and no folder path (they live at the collection
            root), so Angular's chat branches showed only the message text. */}
        {chat ? (
          chatText && <small className={`text-muted ${styles.chatText}`}>{chatText}</small>
        ) : (
          <>
            {isEmail ? (
              <h5 className={styles.subheading}>{resource.title || 'No Subject'}</h5>
            ) : (
              hasTitle && resource.title && <h5 className={styles.subheading}>{resource.title}</h5>
            )}
            {showCollection && resource.collectionName && (
              <small className={`text-muted ${styles.metaLine}`}>
                <i className="far fa-hdd" /> {resource.collectionName}
              </small>
            )}
            <small className={`text-muted ${styles.metaLine}`}>
              <i className="far fa-folder" /> {resource.folders || '/'}
            </small>
          </>
        )}
      </div>
      {hasRightColumn && (
        <div className={styles.rightColumn}>
          {stamp != null && (
            <small className={`text-primary ${styles.dateStamp}`}>
              {stamp}
            </small>
          )}
          {hasThumbnail && (
            <img
              ref={thumbRef}
              src={resource.directPdfThumbnailPath!}
              alt="preview"
              className="img-thumbnail"
              onMouseEnter={showPreview}
              onMouseLeave={() => setPreview(null)}
            />
          )}
          {preview && thumbRef.current && createPortal(
            <div
              className={`${styles.thumbnailPreview} ${preview.flip ? styles.placementRight : styles.placementLeft}`}
              style={{
                top: preview.top,
                left: preview.left,
                transform: preview.flip ? 'translateY(-50%)' : 'translate(-100%, -50%)',
              }}
            >
              <img src={resource.directPdfThumbnailPath!} alt="preview enlarged" />
            </div>,
            thumbRef.current.ownerDocument.body
          )}
        </div>
      )}
    </div>
  );
}
